import React from 'react'
import { PureQueryOptions } from 'apollo-client'
import { Mutation } from 'react-apollo'
import gql from 'graphql-tag'
import { UpdateStatusAction } from './update-status-action'
import { Vulnerability } from './status-action'
import { Role } from '@contrast-security-inc/base-types'

interface UpdateProps {
  readonly vulnerabilities: Vulnerability[]
  readonly organizationID: string
  readonly refetchQueries: PureQueryOptions[]
  readonly refresh: () => Promise<any>
  readonly applicationRole: Role
  readonly isStatusApprovalEnabled: boolean
  readonly menuPlacement: 'top' | 'bottom'
}

interface BulkUpdateResponse {
  readonly _placeholder: boolean
}

interface SingleUpdateResponse {
  readonly id: string
  readonly status: string
}

interface BulkUpdateVars {
  readonly organizationID: string
  readonly ids: string[]
  readonly status: string
  readonly substatus?: string
  readonly reasoning?: string
}

interface SingleUpdateVars {
  readonly organizationID: string
  readonly id: string
  readonly status: string
  readonly substatus?: string
  readonly reasoning?: string
}

export function UpdateStatusesAction({
  applicationRole,
  organizationID,
  refresh,
  refetchQueries,
  vulnerabilities,
  isStatusApprovalEnabled,
  menuPlacement
}: UpdateProps) {
  if (vulnerabilities.length === 1) {
    const vulnerability = vulnerabilities[0]
    return (
      <Mutation<SingleUpdateResponse, SingleUpdateVars>
        mutation={gql`
          mutation UpdateVulnerabilityStatus(
            $organizationID: ID!
            $id: ID!
            $status: VulnerabilityStatus!
            $substatus: String
            $reasoning: String
          ) {
            updateVulnerabilityStatus(
              input: {
                organizationID: $organizationID
                id: $id
                status: $status
                substatus: $substatus
                reasoning: $reasoning
              }
            ) {
              id
              status
            }
          }
        `}
      >
        {(updateStatus) => (
          <UpdateStatusAction
            menuPlacement={menuPlacement}
            organizationID={organizationID}
            vulnerabilities={[vulnerability]}
            applicationRole={applicationRole}
            isStatusApprovalEnabled={isStatusApprovalEnabled}
            updateStatus={({ status, substatus, reasoning }) =>
              updateStatus({
                variables: {
                  organizationID,
                  id: vulnerability.id,
                  status: status.name,
                  substatus: substatus && substatus.name,
                  reasoning
                },
                refetchQueries
              }).then(refresh)
            }
          />
        )}
      </Mutation>
    )
  }

  return (
    <Mutation<BulkUpdateResponse, BulkUpdateVars>
      mutation={gql`
        mutation UpdateVulnerabilitiesStatus(
          $organizationID: ID!
          $ids: [ID!]!
          $status: VulnerabilityStatus!
          $substatus: String
          $reasoning: String
        ) {
          updateVulnerabilitiesStatus(
            input: {
              organizationID: $organizationID
              ids: $ids
              status: $status
              substatus: $substatus
              reasoning: $reasoning
            }
          ) {
            _placeholder
          }
        }
      `}
    >
      {(updateStatuses) => (
        <UpdateStatusAction
          menuPlacement={menuPlacement}
          organizationID={organizationID}
          vulnerabilities={vulnerabilities}
          applicationRole={applicationRole}
          isStatusApprovalEnabled={isStatusApprovalEnabled}
          updateStatus={({ status, substatus, reasoning }) =>
            updateStatuses({
              variables: {
                organizationID,
                ids: vulnerabilities.map((v) => v.id),
                status: status.name,
                substatus: substatus && substatus.name,
                reasoning
              },
              refetchQueries
            }).then(refresh)
          }
        />
      )}
    </Mutation>
  )
}

interface UpdateWithFilterProps {
  readonly organizationID: string
  readonly refetchQueries: PureQueryOptions[]
  readonly excludedVulnerabilities: Vulnerability[]
  readonly refresh: () => Promise<any>
  readonly filters: {
    readonly filterText: string
    readonly quickFilter: string
    readonly aggregations: Record<string, string[]>
  }
  readonly additionalFilters?: { readonly [key: string]: any }
  readonly applicationRole: Role
  readonly menuPlacement: 'top' | 'bottom'
  readonly isStatusApprovalEnabled: boolean
}

interface UpdateWithFilterVars {
  readonly excludedVulnerabilities: string[]
  readonly status: string
  readonly substatus?: string
  readonly reasoning?: string
  readonly organizationID: string
  readonly filters: Record<string, string[]> & {
    readonly filterText: string
    readonly quickFilter: string
  }
}

export function UpdateStatusesWithFilterAction({
  organizationID,
  refetchQueries,
  excludedVulnerabilities,
  refresh,
  filters,
  additionalFilters,
  applicationRole,
  menuPlacement,
  isStatusApprovalEnabled
}: UpdateWithFilterProps) {
  return (
    <Mutation<BulkUpdateResponse, UpdateWithFilterVars>
      mutation={gql`
        mutation updateVulnerabilitiesStatusByFilters(
          $organizationID: ID!
          $filters: VulnerabilityFilterArg!
          $excludedVulnerabilities: [ID!]!
          $status: VulnerabilityStatus!
          $substatus: String
          $reasoning: String
        ) {
          updateVulnerabilitiesStatusByFilters(
            input: {
              organizationID: $organizationID
              filters: $filters
              excludedVulnerabilities: $excludedVulnerabilities
              status: $status
              substatus: $substatus
              reasoning: $reasoning
            }
          ) {
            _placeholder
          }
        }
      `}
    >
      {(updateStatuses) => (
        <UpdateStatusAction
          organizationID={organizationID}
          vulnerabilities={[]}
          filters={filters}
          additionalFilters={additionalFilters}
          applicationRole={applicationRole}
          menuPlacement={menuPlacement}
          isStatusApprovalEnabled={isStatusApprovalEnabled}
          updateStatus={({ status, substatus, reasoning }) =>
            updateStatuses({
              variables: {
                organizationID,
                excludedVulnerabilities: excludedVulnerabilities.map(
                  (v) => v.id
                ),
                substatus: substatus && substatus.name,
                status: status.name,
                reasoning,
                filters: Object.assign({}, filters.aggregations, {
                  quickFilter: filters.quickFilter,
                  filterText: filters.filterText,
                  ...additionalFilters
                })
              },
              refetchQueries
            }).then(refresh)
          }
        />
      )}
    </Mutation>
  )
}
